import React, { useState, useEffect } from 'react';
import { fetchDashboardStats, fetchSales, fetchTanks, fetchCustomers, fetchDashboardPSStats, fetchDashboardMaterialStats, fetchDipReadings, fetchProducts, fetchPurchases } from '../../services/api';
import { fetchPumps } from '../../services/api';
import './Dashboard.css';

function StatCard({ title, value, change, up, icon, color }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">
        <i className={`bi ${icon}`} style={{ color: color, fontSize: '28px' }}></i>
      </div>
      <div className="stat-info">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        {change && (
          <div className={`stat-change ${up ? 'up' : 'down'}`}>
            {up ? '▲' : '▼'} {change}
          </div>
        )}
      </div>
    </div>
  );
}

const getProductInfo = (name = '') => {
  const n = name.toUpperCase();
  if (n.includes('PETROL') || n.includes('MS') || n.includes('PERTOL')) {
    return { label: 'Petrol/MS', color: '#f59e0b' }; // Amber/Orange
  }
  if (n.includes('DIESEL') || n.includes('HSD')) {
    return { label: 'Diesel/HSD', color: '#0369a1' }; // Deep Professional Blue
  }
  if (n.includes('CNG')) {
    return { label: 'CNG', color: '#10b981' }; // Emerald Green
  }
  if (n.includes('OIL')) {
    return { label: 'Oil/Lubricants', color: '#8b5cf6' }; // Vibrant Violet
  }
  return { label: name, color: '#64748b' };
};

function MiniBar({ data }) {
  if (!data || data.length === 0) return <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>No sales data this week</div>;
  const max = Math.max(...data.map(d => (parseFloat(d.Diesel || 0) + parseFloat(d.Petrol || 0) + parseFloat(d.CNG || 0))));
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div key={i} className="bar-group">
          <div className="bars">
            <div className="bar bar-diesel" style={{ height: `${(parseFloat(d.Diesel || 0) / (max || 1)) * 80}px` }} title={`Diesel: ₹${d.Diesel}`} />
            <div className="bar bar-petrol" style={{ height: `${(parseFloat(d.Petrol || 0) / (max || 1)) * 80}px` }} title={`Petrol: ₹${d.Petrol}`} />
            <div className="bar bar-cng" style={{ height: `${(parseFloat(d.CNG || 0) / (max || 1)) * 80}px` }} title={`CNG: ₹${d.CNG}`} />
          </div>
          <div className="bar-label">{d.day}</div>
        </div>
      ))}
    </div>
  );
}

function MonthlyComparisonBar({ data }) {
  if (!data || data.length === 0) return <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>No data available</div>;
  const max = Math.max(...data.map(d => Math.max(parseFloat(d.Purchase || 0), parseFloat(d.Sales || 0))));
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div key={i} className="bar-group">
          <div className="bars">
            <div className="bar bar-purchase" style={{ height: `${(parseFloat(d.Purchase || 0) / (max || 1)) * 80}px` }} title={`Purchase: ₹${d.Purchase}`} />
            <div className="bar bar-sales" style={{ height: `${(parseFloat(d.Sales || 0) / (max || 1)) * 80}px` }} title={`Sales: ₹${d.Sales}`} />
          </div>
          <div className="bar-label">{d.month}</div>
        </div>
      ))}
    </div>
  );
}

function FuelTank({ name, current, capacity, color }) {
  const pct = Math.min(100, Math.round((current / (capacity || 10000)) * 100));
  return (
    <div className="fuel-tank-container">
      <div className="tank-visual">
        <div className="tank-glass"></div>
        <div className="tank-liquid" style={{ height: `${pct}%`, background: color }}></div>
      </div>
      <div className="tank-info">
        <div className="tank-name">{name}</div>
        <div className="tank-qty">{current.toLocaleString()} L</div>
        <div className="tank-pct">{pct}% Full</div>
      </div>
    </div>
  );
}

export default function Dashboard({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [stats, setStats] = useState({
    todaySales: { value: '₹0', change: '0%', up: true },
    totalPumps: { value: '0', change: 'Active', up: true },
    fuelStock: { value: '0 L', change: '0%', up: true },
    creditPending: { value: '₹0', change: '0 parties', up: true },
  });
  const [tankLevels, setTankLevels] = useState([]);
  const [purchaseVsSales, setPurchaseVsSales] = useState([]);
  const [purchaseMaterial, setPurchaseMaterial] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, [companyId]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      // 1. Try dedicated dashboard API (non-blocking)
      let dashRes = { success: false };
      try {
        const [statsRes, psRes, matRes] = await Promise.all([
          fetchDashboardStats(companyId).catch(() => ({ success: false })),
          fetchDashboardPSStats(companyId).catch(() => ({ success: false })),
          fetchDashboardMaterialStats(companyId).catch(() => ({ success: false }))
        ]);
        dashRes = statsRes;
        if (psRes.success && psRes.PurchaseVsSales) {
          setPurchaseVsSales(psRes.PurchaseVsSales.slice(0, 7).reverse());
        }
        if (matRes.success && matRes.PurchaseMaterial) {
          setPurchaseMaterial(matRes.PurchaseMaterial);
        }
      } catch (dashError) {
        console.warn("Dedicated dashboard API failed, falling back to manual calculations", dashError);
      }

      const purchaseMatArr = Array.isArray(purchaseMaterial) ? purchaseMaterial : [];
      // Material Purchase is AMOUNT, not Liters.
      const totalMatPurchaseAmount = purchaseMatArr.reduce((s, m) => s + parseFloat(m.Total || 0), 0);

      // 2. Fetch supporting data for calculations/fallback
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 6);

      const [salesRes, tanksRes, custRes, pumpsRes, dipRes, prodRes, purchaseRes] = await Promise.all([
        fetchSales({
          Companyid: companyId,
          FromDate: formatDateForAPI(fromDate),
          ToDate: formatDateForAPI(new Date())
        }).catch(() => ({ success: false, Response: [] })),

        fetchTanks(companyId).catch(() => ({ success: false, Response: [] })),

        fetchCustomers(companyId).catch(() => ({ success: false, Response: [] })),

        fetchPumps(companyId).catch(() => ({ success: false, Response: [] })),

        fetchDipReadings(companyId).catch(() => ({ success: false, Response: [] })),

        fetchProducts(companyId).catch(() => ({ success: false, Response: [] })),

        fetchPurchases({
          Companyid: companyId,
          FromDate: formatDateForAPI(fromDate),
          ToDate: formatDateForAPI(new Date())
        }).catch(() => ({ success: false, Response: [] }))
      ]);

      const custArr = Array.isArray(custRes.Response) ? custRes.Response : [];
      const creditCusts = custArr.filter(c => parseFloat(c.Balance || 0) < 0).length;
      const creditTotal = custArr.reduce((s, c) => s + Math.abs(Math.min(0, parseFloat(c.Balance || 0))), 0);

      const salesArr = Array.isArray(salesRes.Response) ? salesRes.Response : [];
      const purchaseArr = Array.isArray(purchaseRes.Response) ? purchaseRes.Response : [];
      const pumpsArr = Array.isArray(pumpsRes.Response) ? pumpsRes.Response : [];
      const tanksArr = Array.isArray(tanksRes.Response) ? tanksRes.Response : [];

      const dipArr = Array.isArray(dipRes.Response) ? dipRes.Response : [];
      const prodList = Array.isArray(prodRes.Response) ? prodRes.Response : [];

      // Map latest stock from Dip Readings
      const stockMap = {}; // TankName -> Stock
      const sortedDips = [...dipArr].sort((a, b) => (b.Dipid || 0) - (a.Dipid || 0));
      sortedDips.forEach(d => {
        if (!stockMap[d.TankName]) {
          stockMap[d.TankName] = parseFloat(d.ClosingReading || 0);
        }
      });

      // Fallback: Calculate stock from Capacity + Purchases - Sales
      const purchaseLitersMap = {};
      const salesLitersMap = {};

      purchaseMatArr.forEach(m => {
        const info = getProductInfo(m.Material);
        const prodMatch = prodList.find(p => getProductInfo(p.Name).label === info.label);
        const rate = parseFloat(prodMatch?.Rate || 100);
        const estLiters = parseFloat(m.Total || 0) / rate;
        purchaseLitersMap[info.label] = (purchaseLitersMap[info.label] || 0) + estLiters;
      });

      salesArr.forEach(s => {
        const info = getProductInfo(s.Product || s.ProductName);
        const prodMatch = prodList.find(p => getProductInfo(p.Name).label === info.label);
        const rate = parseFloat(prodMatch?.Rate || 100);
        const estLiters = parseFloat(s.Amount || 0) / rate;
        salesLitersMap[info.label] = (salesLitersMap[info.label] || 0) + estLiters;
      });

      const totalStock = tanksArr.reduce(
        (sum, t) => {
          const info = getProductInfo(t.ProductName);
          const capacity = parseFloat(t.Capacity || 5000);
          const stock = stockMap[t.TankName] || (capacity + (purchaseLitersMap[info.label] || 0) - (salesLitersMap[info.label] || 0));
          return sum + stock;
        },
        0
      );

      // Aggregate Weekly Sales Trend
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        last7Days.push({
          day: days[d.getDay()],
          dateStr: formatDateForAPI(d),
          Diesel: 0,
          Petrol: 0,
          CNG: 0
        });
      }

      salesArr.forEach(s => {
        const dayObj = last7Days.find(d => d.dateStr === s.Date1);
        if (dayObj) {
          const prod = (s.Product || s.ProductName || '').toUpperCase();
          const amt = parseFloat(s.Amount || 0);
          if (prod.includes('DIESEL') || prod.includes('HSD')) dayObj.Diesel += amt;
          else if (prod.includes('PETROL') || prod.includes('MS')) dayObj.Petrol += amt;
          else if (prod.includes('CNG')) dayObj.CNG += amt;
        }
      });
      setWeeklyTrend(last7Days);

      if (dashRes.success && dashRes.TopView) {

        const tv = dashRes.TopView;

        setStats({
          todaySales: {
            value: `₹${Number(tv.SalesAmount || 0).toLocaleString()}`,
            change: '',
            up: true
          },

          totalPumps: {
            value: pumpsArr.length.toString(),
            change: 'Active',
            up: true
          },

          fuelStock: {
            value: `${totalStock.toLocaleString()} L`,
            change: '',
            up: true
          },

          creditPending: {
            value: `₹${creditTotal.toLocaleString()}`,
            change: `${creditCusts} parties`,
            up: creditTotal > 0
          }
        });

        setTankLevels(tanksArr.map(t => {
          const info = getProductInfo(t.ProductName);
          const capacity = parseFloat(t.Capacity || 5000);
          const stock = stockMap[t.TankName] || (capacity + (purchaseLitersMap[info.label] || 0) - (salesLitersMap[info.label] || 0));
          return {
            name: `${t.ProductName} (${t.TankName || t.TankNo})`,
            current: stock,
            capacity: capacity,
            color: info.color
          };
        }));

        return;
      }

      const todaySalesVal = salesArr
        .filter(s => s.Date1 === formatDateForAPI(new Date()))
        .reduce((s, r) => s + parseFloat(r.Amount || 0), 0);

      setStats({
        todaySales: {
          value: `₹${todaySalesVal.toLocaleString()}`,
          change: '',
          up: true
        },

        totalPumps: {
          value: pumpsArr.length.toString(),
          change: 'Active',
          up: true
        },

        fuelStock: {
          value: `${totalStock.toLocaleString()} L`,
          change: '',
          up: true
        },

        creditPending: {
          value: `₹${creditTotal.toLocaleString()}`,
          change: `${creditCusts} parties`,
          up: creditTotal > 0
        }
      });
      setTankLevels(tanksArr.map(t => {
        const info = getProductInfo(t.ProductName);
        const capacity = parseFloat(t.Capacity || 5000);
        const stock = stockMap[t.TankName] || (capacity + (purchaseLitersMap[info.label] || 0) - (salesLitersMap[info.label] || 0));
        return {
          name: `${t.ProductName} (${t.TankName || t.TankNo})`,
          current: stock,
          capacity: capacity,
          color: info.color
        };
      }));

      // Combine Sales and Purchases for Recent Transactions
      const allTxns = [
        ...salesArr.map(s => ({
          id: s.IDS || s.Recid || 'S',
          date: s.Date1 || s.date,
          type: 'Sale',
          amount: parseFloat(s.Amount || 0),
          status: 'Saved',
          timestamp: s.Date1 ? new Date(s.Date1.split('-').reverse().join('-')).getTime() : 0
        })),
        ...purchaseArr.map(p => ({
          id: p.BillNo || p.bill_no || 'P',
          date: p.Date1 || p.date,
          type: 'Purchase',
          amount: parseFloat(p.Total || p.Amount || 0),
          status: 'Received',
          timestamp: p.Date1 ? new Date(p.Date1.split('-').reverse().join('-')).getTime() : 0
        }))
      ];

      setRecentTxns(allTxns
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 10)
      );

    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  function formatDateForAPI(d) {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [day, month, year].join('-');
  }
  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h2 className="page-title">Dashboard</h2>
        <span className="date-badge">
          <i className="bi bi-calendar-event" style={{ marginRight: '6px' }}></i>
          {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">

        <StatCard
          title="Today's Sales"
          value={stats.todaySales.value}
          change={stats.todaySales.change}
          up={stats.todaySales.up}
          icon="bi-cash-stack"
          color="#28a745"
        />

        <StatCard
          title="Active Pumps"
          value={stats.totalPumps.value}
          change={stats.totalPumps.change}
          up={stats.totalPumps.up}
          icon="bi-fuel-pump"
          color="#0d6efd"
        />

        <StatCard
          title="Fuel in Stock"
          value={stats.fuelStock.value}
          change={stats.fuelStock.change}
          up={stats.fuelStock.up}
          icon="bi-droplet-half"
          color="#fd7e14"
        />

        <StatCard
          title="Credit Pending"
          value={stats.creditPending.value}
          change={stats.creditPending.change}
          up={stats.creditPending.up}
          icon="bi-credit-card"
          color="#dc3545"
        />

      </div>

      <div className="dashboard-row">

        {/* Weekly Sales Chart */}
        <div className="section-card chart-card">
          <div className="section-header">Weekly Sales Trend (₹)</div>
          <div style={{ padding: '16px' }}>
            <MiniBar data={weeklyTrend} />
            <div className="chart-legend">
              <span className="legend-item"><span className="dot dot-diesel" />Diesel</span>
              <span className="legend-item"><span className="dot dot-petrol" />Petrol</span>
              <span className="legend-item"><span className="dot dot-cng" />CNG</span>
            </div>
          </div>
        </div>

        {/* Purchase vs Sales Comparison */}
        <div className="section-card chart-card">
          <div className="section-header">Monthly Purchase vs Sales (₹)</div>
          <div style={{ padding: '16px' }}>
            <MonthlyComparisonBar data={purchaseVsSales} />
            <div className="chart-legend">
              <span className="legend-item"><span className="dot bar-purchase-dot" />Purchase</span>
              <span className="legend-item"><span className="dot bar-sales-dot" />Sales</span>
            </div>
          </div>
        </div>

      </div>

      <div className="dashboard-row" style={{ gridTemplateColumns: '1fr' }}>
        {/* Purchase Material Summary */}
        <div className="section-card">
          <div className="section-header">Material Purchase Summary</div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
            {purchaseMaterial.length > 0 ? purchaseMaterial.map((m, idx) => (
              <div key={idx} className="stat-card" style={{ boxShadow: 'none', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="stat-info">
                  <div className="stat-title">{m.Material || 'Unknown'}</div>
                  <div className="stat-value" style={{ fontSize: '18px' }}>₹{Number(m.Total || 0).toLocaleString()}</div>
                </div>
              </div>
            )) : (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', gridColumn: '1 / -1' }}>No purchase data found</div>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-row">

        {/* Fuel Stock */}
        <div className="section-card" style={{ gridColumn: '1 / -1' }}>
          <div className="section-header">Tank Stock Levels</div>
          <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px' }}>
            {tankLevels.length > 0 ? tankLevels.map((t) => (
              <FuelTank
                key={t.name}
                name={t.name}
                current={t.current}
                capacity={t.capacity}
                color={t.color}
              />
            )) : (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', gridColumn: '1 / -1', padding: '20px' }}>Loading tank data...</div>
            )}
          </div>
        </div>

      </div>

      {/* Recent Transactions
      <div className="section-card">
        <div className="section-header">Recent Transactions</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{textAlign:'left'}}>Txn ID</th>
                <th style={{textAlign:'left'}}>Date</th>
                <th style={{textAlign:'left'}}>Type</th>
                <th>Amount (₹)</th>
                <th style={{textAlign:'left'}}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentTxns.map((t) => (
                <tr key={t.id}>
                  <td style={{fontFamily:'monospace', color:'var(--primary)'}}>{t.id}</td>
                  <td>{t.date}</td>
                  <td>{t.type}</td>
                  <td className="right-align">₹{t.amount.toLocaleString()}</td>
                  <td>
                    <span className={`badge ${
                      t.status === 'Saved'    ? 'badge-success' :
                      t.status === 'Pending'  ? 'badge-warning' :
                      t.status === 'Received' ? 'badge-info'    :
                      'badge-primary'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

    </div>
  );
}